import { ButtonWithDropdown, toast } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { Button, TextInput } from "risa-oasis-ui_v2";
import { runPaOrderUpdateWithRules } from "../../../../api/postCall/runPaOrderUpdateWithRules";
import ConditionsCard from "../../../../components/conditionsCrad/conditionsCard";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import AddNewPocAssignmentModal from "../../../../components/modals/AddNewPocAssignmentModal/addNewPocAssignment";
import DeleteStepConfirmModal from "../../../../components/modals/DeleteStepConfirmModal/deleteStepConfirmModal";
import QueryBuilder from "../../../../components/queryBuilder/QueryBuilder";
import { CmmInputUniqueValuesProvider } from "../../../../context/cmmInputUniqueValuesContext";
import type { PocAssignmentTrigger } from "../../../../data-model/pocAssignmentRule";
import { ModalId } from "../../../../enums/modalId";
import { setOpenedModalId } from "../../../../redux/slice/modalSliceNew";
import { AppDispatch } from "../../../../redux/store/store";
import UpdateGroupModal from "../pocConfiguration/components/UpdateGroupModal";
import { usePocAssignmentConfig } from "./usePocAssignmentConfig";
import { usePocAssignmentRules } from "./usePocAssignmentRules";

const formatTriggerLabel = (trigger: PocAssignmentTrigger): string => {
  const value = (trigger as { value?: string }).value;
  return value ? `${trigger.type} (${value})` : trigger.type;
};

const PocAssignmentInner = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: pocDoc, loading: configLoading } = usePocAssignmentConfig();
  const {
    rules,
    conditionConfig,
    configKey,
    conditionTitle,
    isDirty,
    saving,
    ruleLoadedVersion,
    handleAddNewRuleClick,
    openModalForEditRule,
    handleCloseModal,
    handleRuleConditionChange,
    handleDeleteRule,
    initialFormDataForModal,
    handleModalSave,
    handleSave,
    handleDiscard,
    lastSavedMeta,
    editContext,
  } = usePocAssignmentRules(pocDoc);

  const [pendingDeleteRuleId, setPendingDeleteRuleId] = useState<string | null>(
    null,
  );
  const [running, setRunning] = useState(false);

  const handleRunRules = async () => {
    setRunning(true);
    try {
      await runPaOrderUpdateWithRules([
        "/configurations/pharmacy_order_updates/rules/poc_assignment",
      ]);
      toast.success("Rules executed successfully");
    } catch {
      toast.error("Failed to run rules. Please try again.");
    } finally {
      setRunning(false);
    }
  };

  const handleDeleteRuleClick = (ruleId: string) => {
    setPendingDeleteRuleId(ruleId);
  };

  const handleConfirmDeleteRule = () => {
    if (pendingDeleteRuleId) {
      handleDeleteRule(pendingDeleteRuleId);
    }
    setPendingDeleteRuleId(null);
  };

  const handleCancelDeleteRule = () => {
    setPendingDeleteRuleId(null);
  };

  const formattedLastUpdated =
    lastSavedMeta?.updated_at != null
      ? moment(lastSavedMeta.updated_at).format("DD MMM YYYY HH:mm")
      : null;

  if (configLoading) {
    return (
      <div className="flex min-h-0 flex-1 flex-col bg-primaryGray-16 p-4">
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
          <LoaderMessage message="Loading POC assignment rules..." />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-primaryGray-16 p-4">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden rounded-lg bg-white p-6">
        <div className="flex flex-shrink-0 flex-col gap-3 border-b border-primaryGray-16 pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="text-h11 font-bold text-primaryGray-1">
              POC Assignment
            </div>
            <div className="mt-1 text-small text-primaryGray-4">
              Configure rules that decide which POC receives an order.
            </div>
          </div>
          <div className="flex flex-shrink-0">
            <ButtonWithDropdown
              buttonType="secondary"
              disabled={saving}
              size="medium"
              menuData={[
                {
                  id: "update-assignment-groups",
                  label: "Update",
                  onClick: () =>
                    dispatch(
                      setOpenedModalId({ id: ModalId.UPDATE_GROUP_MODAL }),
                    ),
                },
              ]}
            >
              Groups
            </ButtonWithDropdown>
          </div>
        </div>

        <div className="mb-1 mt-2 flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-y-contain">
          <div className="flex flex-col gap-2 pb-4">
            {rules.map((rule) => (
              <ConditionsCard
                key={rule.id}
                header={rule.label}
                subHeader={rule.subtext ?? "POC assignment rule"}
                headerClickable={false}
                defaultExpanded
                onStageSettingClick={() => openModalForEditRule(rule.id)}
                onDeleteStageClick={() => handleDeleteRuleClick(rule.id)}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full bg-orange-500" />
                      <div className="text-sm font-semibold uppercase text-secondaryOrange-2">
                        {conditionTitle}
                      </div>
                    </div>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        rule.active
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {rule.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <QueryBuilder
                    key={`qb-${rule.id}-${ruleLoadedVersion}-${configKey}`}
                    value={rule.condition}
                    onChange={(q) => handleRuleConditionChange(rule.id, q)}
                    config={conditionConfig}
                  />

                  <div className="flex flex-col gap-1 rounded-md border border-primaryGray-14 p-3">
                    <div className="text-tiny font-semibold uppercase text-primaryGray-8">
                      Triggers
                    </div>
                    <div className="text-sm">
                      {rule.triggers.length === 0
                        ? "—"
                        : rule.triggers.map(formatTriggerLabel).join(", ")}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 rounded-md border border-primaryGray-14 p-3">
                    <div className="text-tiny font-semibold uppercase text-primaryGray-8">
                      Actions
                    </div>
                    {rule.actions.length === 0 ? (
                      <div className="text-sm text-primaryGray-12">—</div>
                    ) : (
                      rule.actions.map((action, idx) => (
                        <div
                          key={`${rule.id}-action-${idx}`}
                          className="grid grid-cols-3 gap-3"
                        >
                          <TextInput
                            id={`${rule.id}-action-type-${idx}`}
                            label={idx === 0 ? "Type" : ""}
                            defaultValue={action.type || "—"}
                            readOnly
                          />
                          <TextInput
                            id={`${rule.id}-action-field-${idx}`}
                            label={idx === 0 ? "Field" : ""}
                            defaultValue={action.field || "—"}
                            readOnly
                          />
                          <TextInput
                            id={`${rule.id}-action-value-${idx}`}
                            label={idx === 0 ? "Value" : ""}
                            defaultValue={
                              Array.isArray(action.value)
                                ? action.value.length === 0
                                  ? "—"
                                  : action.value.join(", ")
                                : action.value || "—"
                            }
                            readOnly
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </ConditionsCard>
            ))}

            <div
              role="button"
              tabIndex={0}
              className="flex cursor-pointer flex-row items-center justify-center gap-2 rounded-md border border-primaryGray-14 p-4 transition-colors hover:border-primaryGray-12 hover:bg-primaryGray-16"
              onClick={handleAddNewRuleClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleAddNewRuleClick();
                }
              }}
            >
              <div>+</div>
              <div className="text-sm">Add New Rule</div>
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-row items-center justify-between gap-4 border-t border-primaryGray-16 pt-2">
          <div className="flex flex-row gap-4 text-tiny">
            {lastSavedMeta != null &&
            (lastSavedMeta.updated_by != null ||
              lastSavedMeta.updated_at != null) ? (
              <div className="mt-1">
                Last updated:{" "}
                {lastSavedMeta.updated_at != null &&
                  formattedLastUpdated != null && (
                    <span className="font-semibold">
                      {formattedLastUpdated}
                    </span>
                  )}
                {lastSavedMeta.updated_at != null &&
                  formattedLastUpdated != null &&
                  lastSavedMeta.updated_by != null && <span> by </span>}
                {lastSavedMeta.updated_by != null && (
                  <span className="font-semibold">
                    {lastSavedMeta.updated_by}
                  </span>
                )}
              </div>
            ) : (
              <div className="mt-1 text-primaryGray-12">Not saved yet</div>
            )}
          </div>
          <div className="flex flex-row gap-2">
            <Button
              buttonType="secondary"
              size="medium"
              onClick={handleRunRules}
              disabled={running}
              children={running ? "Running..." : "Run"}
            />
            <Button
              buttonType="secondary"
              size="medium"
              onClick={handleDiscard}
              disabled={!isDirty}
              children="Cancel"
            />
            <Button
              buttonType="primary"
              size="medium"
              onClick={handleSave}
              disabled={!isDirty || saving}
              children={saving ? "Saving..." : "Save"}
            />
          </div>
        </div>
      </div>

      <AddNewPocAssignmentModal
        key={editContext ? `edit-${editContext.ruleId}` : "add"}
        conditionConfig={conditionConfig}
        initialData={initialFormDataForModal}
        onCancel={handleCloseModal}
        onClose={handleCloseModal}
        onSave={handleModalSave}
      />

      {pendingDeleteRuleId != null && (
        <DeleteStepConfirmModal
          onConfirm={handleConfirmDeleteRule}
          onClose={handleCancelDeleteRule}
        />
      )}

      <UpdateGroupModal />
    </div>
  );
};

/** POC Assignment page — rules at `/configurations/pharmacy_order_updates/rules/poc_assignment`. */
const PocAssignment = () => {
  return (
    <CmmInputUniqueValuesProvider>
      <div className="flex h-full min-h-0 flex-1 flex-col">
        <PocAssignmentInner />
      </div>
    </CmmInputUniqueValuesProvider>
  );
};

export default PocAssignment;
